/**
 * Enum for the steps in the initial onboarding journey.
 */
export enum STEP_INITIAL_JOURNEY {
  termOfAcceptance,
  welcome,
  financialData,
  managementEntryExit,
  reports,
  creditCard,
  investiment,
  lineCredit,
  openFinance
}

/**
 * Interface representing the structure of an onboarding carousel modal.
 */
export interface OnboardingCarouselModal {
  id: string;
  checked: boolean;
  image?: string;
  body?: string;
  headerTitle: string;
  lastStep?: boolean;
  labelPrimaryButton?: string;
  labelSecondaryButton?: string;
  secondTextAccepTerm?: string;
  showCloseModal?: boolean;
  /** Título da tela de aceite (modal "Acesse GRATUITAMENTE o Meu CAIXA") */
  welcomeTitle?: string;
  /** Subtítulo da tela de boas-vindas */
  welcomeSubtitle?: string;
  /** Segundo parágrafo do subtítulo da tela de boas-vindas — aceita HTML inline (ex: <b>) */
  welcomeSubtitleSecondary?: string;
  /** Título do header quando o usuário abre a sub-tela de termos */
  termsHeaderTitle?: string;
  /** Texto que antecede o link dos Termos de Uso na tela de aceite */
  termsConsentPrefix?: string;
  /** Rótulo do link que abre o termo completo */
  termsLinkLabel?: string;
}

/**
 * Step-by-step onboarding modals.
 */
export const STEP_BY_STEP = {
  [STEP_INITIAL_JOURNEY.termOfAcceptance]: {
    id: 'termOfAcceptance',
    headerTitle: 'Meu CAIXA',
    termsHeaderTitle: 'TERMOS E CONDIÇÕES DE USO',
    checked: true,
    labelPrimaryButton: 'Acessar Meu CAIXA',
    showCloseModal: false,
    welcomeTitle: 'Acesse GRATUITAMENTE o Meu CAIXA',
    welcomeSubtitle:
      'Controle as finanças e contas da sua empresa de forma simples, dentro do Gerenciador CAIXA.',
    welcomeSubtitleSecondary:
      'É <b>gratuito</b>, não altera suas configurações atuais e usa apenas os dados necessários para funcionar.',
    termsConsentPrefix: 'Ao acessar, você declara estar ciente e de acordo com os ',
    termsLinkLabel: 'Termos de Uso',
  },
  [STEP_INITIAL_JOURNEY.welcome]: {
    id: 'welcome',
    headerTitle: 'Boas-vindas ao Meu CAIXA!',
    checked: false,
    image: 'assets/initial-journey/initial-journey.gif',
    showCloseModal: false,
    body: `
      A solução que vai facilitar a sua vida financeira e te ajudar na gestão do dia a dia. O <b>Meu CAIXA</b> é a <b>ferramenta que faltava</b> para você ter total controle sobre as finanças do seu negócio.<br>
      <br>
      Confira as funcionalidades que preparamos para você.
      `,
  },
  [STEP_INITIAL_JOURNEY.financialData]: {
    id: 'financialData',
    headerTitle: 'Meu financeiro',
    checked: false,
    image: 'assets/initial-journey/financial-modal.svg',
    showCloseModal: false,
    body: `
      Gerencie suas finanças com confiança! No painel <b>Meu financeiro</b> você encontra de forma simples o resumo das entradas e saídas do seu negócio, incluindo o que está previsto ou em atraso.
      `,
  },
  [STEP_INITIAL_JOURNEY.managementEntryExit]: {
    id: 'managementEntryExit',
    headerTitle: 'Gestão de Acesso',
    checked: false,
    image: 'assets/initial-journey/management-entry-exit-modal.svg',
    showCloseModal: false,
    body: `
      Tenha controle total sobre o dinheiro do seu negócio! Confira o <b>Fluxo de Caixa</b> e a <b>Lista de Registros</b>, e saiba de tudo que entrou e saiu. Use os filtros se quiser facilitar a análise.
      `,
  },
  [STEP_INITIAL_JOURNEY.reports]: {
    id: 'reports',
    headerTitle: 'Relatórios',
    checked: false,
    image: 'assets/initial-journey/reports-modal.svg',
    showCloseModal: false,
    body: `
      Para uma visão mensal completa e tomar decisões com confiança, use os relatórios de <b>Comparativo de Resultados</b> e <b>Demonstrativo Financeiro Mensal</b>.
      `,
  },
  [STEP_INITIAL_JOURNEY.creditCard]: {
    id: 'creditCard',
    headerTitle: 'Cartões de Crédito',
    checked: false,
    image: 'assets/initial-journey/credit-card-modal.svg',
    showCloseModal: false,
    body: `
      Organize e controle tudo, sem perder tempo! Com <b>Cartões de Crédito</b>, você gerencia todos os cartões de crédito da sua empresa em um só lugar, não importa qual banco.
      `,
  },
  [STEP_INITIAL_JOURNEY.investiment]: {
    id: 'investiment',
    headerTitle: 'Investimentos',
    checked: false,
    image: 'assets/initial-journey/investiment-modal.svg',
    showCloseModal: false,
    body: `
      Visão clara dos seus <b>investimentos</b>! Acompanhe de um jeito fácil todas as informações e saldos dos seus investimentos, de todas as suas contas bancárias
      `,
  },
  [STEP_INITIAL_JOURNEY.lineCredit]: {
    id: 'lineCredit',
    headerTitle: 'Linhas de Crédito',
    checked: false,
    image: 'assets/initial-journey/credit-line-modal.svg',
    showCloseModal: false,
    body: `
      Encontre de forma simples e rápida as informações sobre empréstimos, financiamentos e outros tipos de crédito disponíveis em um só lugar, use <b>Linhas de Crédito</b>.
      `,
  },
  [STEP_INITIAL_JOURNEY.openFinance]: {
    id: 'openFinance',
    headerTitle: 'Open Finance!',
    checked: false,
    image: 'assets/initial-journey/open-finance-modal.svg',
    lastStep: true,
    labelPrimaryButton: 'Conectar contas',
    labelSecondaryButton: 'Já conectei',
    showCloseModal: true,
    body: `
      Agora que você já conhece a ferramenta, o primeiro passo é <b>conectar outras contas bancárias via Open Finance</b>. Assim você garante a visão total que seu negócio precisa.
      `,
  }
}
